/**
 * pic.hide_atc — block a standalone add of a gift product, and block quantity increases.
 * Never removes lines. Free gifts added together with another product must stay in the cart.
 */
(() => {
  if (window.__piHideAtcReady) return;
  window.__piHideAtcReady = true;

  const readConfig = () => {
    const el = document.getElementById('pi-hide-atc-cart-gifts');
    if (!el) return { variantIds: [], lineKeys: [], productVariantIds: [] };
    try {
      return JSON.parse(el.textContent) || {};
    } catch (error) {
      return { variantIds: [], lineKeys: [], productVariantIds: [] };
    }
  };

  const toSet = (list) => new Set((list || []).map((v) => String(v)));

  const isGiftVariantId = (id) => {
    const cfg = readConfig();
    const idStr = String(id || '');
    return toSet(cfg.variantIds).has(idStr) || toSet(cfg.productVariantIds).has(idStr);
  };

  const isGiftLineKey = (key) => toSet(readConfig().lineKeys).has(String(key || ''));

  const giftFormFromTarget = (target) => {
    const form = target?.closest?.(
      'form[data-instant-form-line-item-key], form.i6wjcaUafpoBJq6M6, form[data-pi-gift]'
    );
    if (!form) return null;
    const key = form.getAttribute('data-instant-form-line-item-key');
    const variantId = form.getAttribute('data-instant-form-variant-id');
    if (
      form.classList.contains('pi-cart-line--gift') ||
      form.hasAttribute('data-pi-gift') ||
      isGiftLineKey(key) ||
      isGiftVariantId(variantId)
    ) {
      return form;
    }
    return null;
  };

  const lineItemsFrom = (body) => {
    if (!body || typeof body !== 'object') return [];
    if (Array.isArray(body.items)) return body.items;
    if (body.id) return [body];
    return [];
  };

  // True only when every line in this request is a gift variant.
  // A free gift added in the same request as another product is allowed.
  const isSoleGiftAdd = (body) => {
    const items = lineItemsFrom(body);
    if (!items.length) return false;
    return items.every((item) => isGiftVariantId(item?.id || item?.variant_id));
  };

  // Block Instant quantity-increment on gift lines
  document.addEventListener(
    'click',
    (event) => {
      const increment = event.target?.closest?.(
        '[data-instant-action-type="quantity-increment"], .iy0l8OUHfKV2UWT4I'
      );
      if (!increment) return;
      const giftForm = giftFormFromTarget(increment);
      if (!giftForm) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    },
    true
  );

  // Do not intercept Instant cart Remove. Instant sets quantity 0 and redraws
  // the drawer. Gift lines are cleaned up by pi-gift-bundle when needed.

  // Block Instant ATC / redirect-to-cart on gift PDPs
  document.addEventListener(
    'click',
    (event) => {
      const atc = event.target?.closest?.(
        '[data-instant-action-type="add-to-instant-cart"], [data-pi-atc], .pi-sticky-atc__btn, a[data-instant-action-type="redirect-to-cart"]'
      );
      if (!atc) return;
      const variantId =
        atc.getAttribute('data-instant-action-variant-id') ||
        atc.getAttribute('data-variant-id') ||
        atc.closest('[data-instant-form-variant-id]')?.getAttribute('data-instant-form-variant-id');
      if (!isGiftVariantId(variantId)) return;
      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
    },
    true
  );

  // Block a standalone cart/add of a gift. Mixed adds (paid product + free gift) pass through.
  // Quantity changes may go down or to zero (remove). Only an increase is blocked.
  const originalFetch = window.fetch?.bind(window);
  if (originalFetch) {
    window.fetch = async function (input, init) {
      try {
        const url = typeof input === 'string' ? input : input?.url || '';
        if (/\/cart\/(add|change)(\.js)?/i.test(url) && init?.body) {
          let body = init.body;
          if (typeof body === 'string') {
            try {
              body = JSON.parse(body);
            } catch (error) {
              body = null;
            }
          }
          if (body && /\/cart\/add/i.test(url) && isSoleGiftAdd(body)) {
            const cfg = readConfig();
            const onGiftPdp = (cfg.productVariantIds || []).length > 0;
            const items = lineItemsFrom(body);
            const onlyCurrentPdp = items.every((item) =>
              toSet(cfg.productVariantIds).has(String(item?.id || item?.variant_id || ''))
            );
            if (onGiftPdp && onlyCurrentPdp) {
              return new Response(JSON.stringify({ description: 'Gift product cannot be added on its own' }), {
                status: 422,
                headers: { 'Content-Type': 'application/json' },
              });
            }
          }
          if (body && /\/cart\/change/i.test(url)) {
            const items = lineItemsFrom(body);
            for (const item of items) {
              const id = item?.id || item?.variant_id;
              const key = body.line || body.id || item.key;
              const nextQty = Number(body.quantity);
              if (!(isGiftLineKey(key) || isGiftVariantId(id))) continue;
              const form = document.querySelector(
                `form[data-instant-form-line-item-key="${CSS.escape(String(key))}"]`
              );
              const current =
                Number(form?.querySelector('input[name="quantity"]')?.value) ||
                Number(document.querySelector(`[data-line-key="${CSS.escape(String(key))}"]`)?.value) ||
                1;
              if (!Number.isNaN(nextQty) && nextQty > current) {
                return new Response(JSON.stringify({ description: 'Gift quantity cannot be increased' }), {
                  status: 422,
                  headers: { 'Content-Type': 'application/json' },
                });
              }
            }
          }
        }
      } catch (error) {
        // fall through to original fetch
      }
      return originalFetch(input, init);
    };
  }

  // Theme quantity-input: cap gift lines at current qty
  document.addEventListener(
    'change',
    (event) => {
      const input = event.target?.closest?.('input.quantity-input, input[name="quantity"]');
      if (!input) return;
      const key = input.getAttribute('data-line-key');
      const form = input.closest('form[data-instant-form-line-item-key]');
      const formKey = form?.getAttribute('data-instant-form-line-item-key');
      const variantId = form?.getAttribute('data-instant-form-variant-id');
      if (!isGiftLineKey(key) && !isGiftLineKey(formKey) && !isGiftVariantId(variantId)) return;

      const max = Number(input.getAttribute('data-pi-gift-max') || input.value || 1);
      if (Number(input.value) > max) {
        input.value = String(max);
        input.dispatchEvent(new Event('input', { bubbles: true }));
      }
    },
    true
  );

})();
